import { CheckCircle2, LoaderCircle } from "lucide-react";
import { useState } from "react";

type FormVariant = "consultation" | "support";

type ContactFormProps = {
  variant: FormVariant;
};

type FormState =
  | { status: "idle" }
  | { status: "submitting" }
  | { status: "success"; reference: string }
  | { status: "error"; message: string };

function value(formData: FormData, key: string): string {
  const item = formData.get(key);
  return typeof item === "string" ? item : "";
}

export function ContactForm({ variant }: ContactFormProps) {
  const [state, setState] = useState<FormState>({ status: "idle" });
  const consultation = variant === "consultation";

  async function submitForm(form: HTMLFormElement): Promise<void> {
    setState({ status: "submitting" });
    const formData = new FormData(form);
    const payload = {
      formType: variant,
      name: value(formData, "name"),
      email: value(formData, "email"),
      phone: value(formData, "phone"),
      timezone: value(formData, "timezone"),
      tankSize: value(formData, "tankSize"),
      urgency: value(formData, "urgency") || "routine",
      subject: value(formData, "subject"),
      message: value(formData, "message"),
      website: value(formData, "website"),
      page: window.location.pathname,
      consent: formData.get("consent") === "on",
    };

    try {
      const response = await fetch("/api/forms", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const result: unknown = await response.json();
      const data = typeof result === "object" && result !== null ? result : {};

      if (!response.ok) {
        const message = "error" in data && typeof data.error === "string"
          ? data.error
          : "We could not send your request. Please email support@aquariumhelpline.com.";
        setState({ status: "error", message });
        return;
      }

      const reference = "reference" in data && typeof data.reference === "string" ? data.reference : "received";
      form.reset();
      setState({ status: "success", reference });
    } catch {
      setState({
        status: "error",
        message: "We could not reach the server. Please try again or email support@aquariumhelpline.com.",
      });
    }
  }

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    void submitForm(event.currentTarget);
  }

  if (state.status === "success") {
    return (
      <div className="form-success" role="status">
        <CheckCircle2 aria-hidden="true" />
        <p className="index-label"><span>Request received</span></p>
        <h2 className="display">{consultation ? "We’ll review your aquarium details." : "Your support message is in the queue."}</h2>
        <p>Keep this reference for your records: <strong>{state.reference}</strong></p>
        {consultation ? <p>Use the preparation checklist while you wait so your call can stay focused.</p> : null}
        {consultation ? <a className="text-link" href="/next-steps/">Open the preparation checklist</a> : null}
      </div>
    );
  }

  return (
    <form className="contact-form" onSubmit={handleSubmit}>
      <div className="contact-form__head">
        <span>{consultation ? "Consultation request" : "Support request"}</span>
        <span>Sheet 01</span>
      </div>
      <div className="contact-form__grid">
        <label className="field">
          <span>Name</span>
          <input name="name" type="text" autoComplete="name" minLength={2} maxLength={100} required />
        </label>
        <label className="field">
          <span>Email</span>
          <input name="email" type="email" autoComplete="email" maxLength={254} required />
        </label>
        <label className="field">
          <span>Phone <small>optional</small></span>
          <input name="phone" type="tel" autoComplete="tel" maxLength={30} />
        </label>
        {consultation ? (
          <label className="field">
            <span>Your time zone</span>
            <input name="timezone" type="text" placeholder="Pacific, Eastern, GMT…" maxLength={200} />
          </label>
        ) : (
          <label className="field">
            <span>Subject</span>
            <input name="subject" type="text" minLength={3} maxLength={200} required />
          </label>
        )}
        {consultation ? (
          <label className="field">
            <span>Tank size <small>optional</small></span>
            <input name="tankSize" type="text" placeholder="75 gallons" maxLength={200} />
          </label>
        ) : null}
        {consultation ? (
          <label className="field">
            <span>How urgent is it?</span>
            <select name="urgency" defaultValue="routine">
              <option value="routine">Routine question</option>
              <option value="soon">I need help soon</option>
              <option value="emergency">Possible animal emergency</option>
            </select>
          </label>
        ) : null}
      </div>
      <label className="field">
        <span>{consultation ? "What is happening in the tank?" : "How can we help?"}</span>
        <textarea
          name="message"
          rows={7}
          minLength={10}
          maxLength={5000}
          placeholder={consultation ? "Describe the symptoms, recent changes, water readings, and what you have already tried." : "Include any purchase or consultation details that will help us find your request."}
          required
        />
      </label>
      <label className="field field--honeypot" aria-hidden="true">
        <span>Website</span>
        <input name="website" type="text" tabIndex={-1} autoComplete="off" />
      </label>
      <label className="check-field">
        <input name="consent" type="checkbox" required />
        <span>I agree that Aquarium Helpline may use these details to respond to my request. See the <a href="/privacy/">privacy notice</a>.</span>
      </label>
      {state.status === "error" ? <p className="form-error" role="alert">{state.message}</p> : null}
      <div className="contact-form__submit">
        <button className="submit-button" type="submit" disabled={state.status === "submitting"}>
          {state.status === "submitting" ? <LoaderCircle className="submit-button__spinner" aria-hidden="true" /> : null}
          {state.status === "submitting" ? "Sending…" : consultation ? "Send consultation request" : "Send support message"}
        </button>
        <p className="form-note">Encrypted in transit and saved so nothing is lost if email delivery fails.</p>
      </div>
    </form>
  );
}
